import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  DEFAULT_MOCK_SCENARIO_STATE,
  MOCK_SCENARIOS,
  MockScenarioId,
  MockScenarioState,
  getMockScenarioDefinition
} from './mock-scenarios';

@Injectable({
  providedIn: 'root'
})
export class MockScenarioService {
  private static readonly STORAGE_KEY = 'bus-screen-debug-scenario';

  private readonly stateSubject = new BehaviorSubject<MockScenarioState>(this.readInitialState());

  public readonly state = this.stateSubject.asObservable();
  public readonly scenarios = MOCK_SCENARIOS;

  public get snapshot(): MockScenarioState {
    return this.stateSubject.value;
  }

  public get activeScenario() {
    return getMockScenarioDefinition(this.snapshot.scenarioId);
  }

  public setUseRealApi(useRealApi: boolean): void {
    this.updateState({ useRealApi });
  }

  public setScenario(scenarioId: MockScenarioId): void {
    this.updateState({ scenarioId, useRealApi: false });
  }

  public setLatencyMs(latencyMs: number): void {
    const safeLatency = Number.isFinite(latencyMs) ? Math.max(0, Math.round(latencyMs)) : 0;
    this.updateState({ latencyMs: safeLatency });
  }

  public reset(): void {
    this.updateState({ ...DEFAULT_MOCK_SCENARIO_STATE }, true);
  }

  private readInitialState(): MockScenarioState {
    if (!environment.enableMockTools || typeof localStorage === 'undefined') {
      return { ...DEFAULT_MOCK_SCENARIO_STATE };
    }

    try {
      const rawState = localStorage.getItem(MockScenarioService.STORAGE_KEY);
      if (!rawState) {
        return { ...DEFAULT_MOCK_SCENARIO_STATE };
      }

      const parsedState = JSON.parse(rawState) as Partial<MockScenarioState>;
      const fallback = { ...DEFAULT_MOCK_SCENARIO_STATE };
      return {
        useRealApi: parsedState.useRealApi ?? fallback.useRealApi,
        scenarioId: MOCK_SCENARIOS.some((scenario) => scenario.id === parsedState.scenarioId)
          ? parsedState.scenarioId as MockScenarioId
          : fallback.scenarioId,
        latencyMs: Number.isFinite(parsedState.latencyMs)
          ? Math.max(0, Math.round(parsedState.latencyMs as number))
          : fallback.latencyMs
      };
    } catch {
      return { ...DEFAULT_MOCK_SCENARIO_STATE };
    }
  }

  private updateState(patch: Partial<MockScenarioState>, replace = false): void {
    const nextState = replace
      ? patch as MockScenarioState
      : { ...this.snapshot, ...patch };

    this.stateSubject.next(nextState);
    this.persist(nextState);
  }

  private persist(state: MockScenarioState): void {
    if (!environment.enableMockTools || typeof localStorage === 'undefined') {
      return;
    }

    localStorage.setItem(MockScenarioService.STORAGE_KEY, JSON.stringify(state));
  }
}
